import {
  pathCommandsToSvgPath,
  preparePathCommandsForSvgRender,
  shapeToPathCommands,
} from '@/lib/elements/shape-path-commands'
import {
  effectiveShapeKindId,
  isRoundingEligibleShapeType,
  roundedCornerRadiusFromPercent,
  roundedPathDFromLineCommands,
  roundedRectPathD,
} from '@/lib/object-effects/rounding'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/** The shape fields that decide its outline. */
export type ShapePathSource = Pick<
  ShapeData,
  'type' | 'width' | 'height' | 'path' | 'pathCommands' | 'kindId' | 'roundedCorner'
>

function hasPathString(shape: ShapePathSource): shape is ShapePathSource & { path: string } {
  return typeof shape.path === 'string' && shape.path.trim().length > 0
}

function roundedShapePathD(shape: ShapePathSource): string | null {
  const w = Math.max(0, shape.width)
  const h = Math.max(0, shape.height)

  if (effectiveShapeKindId(shape) === 1) {
    return roundedRectPathD(w, h, roundedCornerRadiusFromPercent(w, h, shape.roundedCorner!))
  }

  const commands =
    Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0
      ? shape.pathCommands
      : shapeToPathCommands(shape.type, w, h)

  return roundedPathDFromLineCommands(shape.type, commands, w, h, shape.roundedCorner!)
}

/**
 * Resolves a shape's SVG `d` from the data it already carries, in priority order:
 *
 * 1. Rounding — runs before `pathCommands` and the cached `path`.
 * 2. Pen paths (user-drawn or API-loaded) — `shape.path` is canonical; `pathCommands` go stale
 *    during node editing since only `path` is updated.
 * 3. `pathCommands`.
 * 4. Any other stored `path` string.
 *
 * Returns `null` when none apply; the caller then falls back to the WASM generator
 * (`getShapePath`). Every branch is synchronous so the caller can apply the result before the
 * browser paints.
 */
export function resolveShapePathD(shape: ShapePathSource): string | null {
  if (
    isRoundingEligibleShapeType(shape.type) &&
    typeof shape.roundedCorner === 'number' &&
    shape.roundedCorner > 0
  ) {
    const rounded = roundedShapePathD(shape)

    if (rounded) {
      return rounded
    }
  }

  if (hasPathString(shape) && (shape.kindId == null || shape.type === 'path')) {
    return shape.path
  }

  if (Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0) {
    const w = Math.max(0, shape.width)
    const h = Math.max(0, shape.height)
    const commands =
      w > 0 && h > 0
        ? preparePathCommandsForSvgRender(shape.pathCommands, w, h)
        : shape.pathCommands
    // For pen paths loaded from the API (kindId set), check whether the stored path string is
    // closed (ends with Z) so the closing segment gets a stroke. pathCommands never includes a
    // close command.
    const isStoredPathClosed =
      shape.type === 'path' && hasPathString(shape) && /Z\s*$/i.test(shape.path.trim())

    return pathCommandsToSvgPath(commands, { close: shape.type !== 'path' || isStoredPathClosed })
  }

  if (hasPathString(shape)) {
    return shape.path
  }

  return null
}

/** Plain rectangle used when path generation throws. */
export function fallbackShapePathD(width: number, height: number): string {
  return `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`
}
